import { type WeekDay } from '../utils/timeFrames';
import { type BlobConfig } from './blob/blobConfigs';
import { type ProjectGroupAccess } from './projectGroupAccess';
import { type Pagination } from './templates';
import { type Timezones } from './timezone';

export enum ProjectType {
    DEFAULT = 'DEFAULT',
    PREVIEW = 'PREVIEW',
}

export enum AiConnectorProviderKey {
    HUBSPOT = 'hubspot',
    CUSTOMERIO = 'customerio',
    WEBENGAGE = 'webengage',
    JIRA = 'jira',
    MOENGAGE = 'moengage',
    PIPEDREAM = 'pipedream',
    COMPOSIO = 'composio',
    CLEVERTAP = 'clevertap',
    NETCORE = 'netcore',
    META_ADS = 'meta_ads',
    GOOGLE_POSTMASTER = 'google_postmaster',
    MIXPANEL = 'mixpanel',
}

export enum AiConnectorStatus {
    NOT_CONFIGURED = 'not_configured',
    ACTIVE = 'active',
    ERROR = 'error',
}

/**
 * `public` — one shared connection per project, usable by every member.
 * `private` — usable only by `connectedByUserUuid`; multiple users can each
 * hold their own private connection to the same app in the same project.
 * Both lanes can coexist: a workspace shared connection plus per-user private ones.
 */
export enum ConnectorVisibility {
    PUBLIC = 'public',
    PRIVATE = 'private',
}

export type AiConnector = {
    providerKey: string;
    hasCredentials: boolean;
    status: AiConnectorStatus;
    updatedAt: string | null;
    visibility: ConnectorVisibility;
    /** The user who created the connection, or null for public connections. */
    connectedByUserUuid: string | null;
};

export type UpsertAiConnectorRequest = {
    credentials: Record<string, string>;
    /** Defaults to public. */
    visibility: ConnectorVisibility;
    /**
     * Targets an existing connection's credentials for an in-place update
     * (rotating a key/token) instead of creating a new one. Omit to create a
     * new connection — a user may hold more than one private connection to
     * the same provider, so omitting this never overwrites an existing row.
     */
    connectionUuid?: string;
    /** Human-readable name, shown when a user has more than one connection to the same provider. */
    label?: string;
};

export type SetConnectorVisibilityRequest = {
    visibility: ConnectorVisibility;
};

/** Admin view of who has connected a given app within a project. */
export type ConnectorRegistryOwnershipEntry = {
    appKey: string;
    connectionMethod: AppConnectorMethod;
    /** null when nobody has connected this app in the project yet. */
    visibility: ConnectorVisibility | null;
    /** Populated only when `visibility` is `private` — one entry per user. */
    connectedUsers: Array<{ userUuid: string; email: string }>;
};

export enum AppConnectorMethod {
    API_REF = 'apiRef',
    PIPEDREAM = 'pipedream',
    COMPOSIO = 'composio',
    GENERIC_MCP = 'genericMcp',
    OAUTH2 = 'oauth2',
}

export type OAuthEndpointsConfig = {
    tokenUrl: string;
    scopes: string[];
    baseApiUrl: string;
    additionalTokenParams?: Record<string, string>;
    authType?: 'client_credentials' | 'jwt_bearer';
};

export type AppConnectorRegistryEntry = {
    appKey: string;
    label: string;
    connectionMethod: AppConnectorMethod;
    apiDocsUrl: string | null;
    mcpServerUrl: string | null;
    description: string | null;
    logoUrl: string | null;
    oauthEndpoints: OAuthEndpointsConfig | null;
};

export type CreateAppConnectorRegistryEntry = {
    appKey: string;
    label: string;
    connectionMethod: AppConnectorMethod;
    apiDocsUrl: string | null;
    mcpServerUrl: string | null;
    description: string | null;
    logoUrl: string | null;
    oauthEndpoints: OAuthEndpointsConfig | null;
};

export type UpdateAppConnectorRegistryEntry = Partial<
    Omit<CreateAppConnectorRegistryEntry, 'appKey'>
>;

/** Which slice of the connector catalog a registry page should return. */
export enum AppConnectorFilterStatus {
    /** Every app in the catalog. */
    ALL = 'all',
    /**
     * Only apps this project has configured — including ones that are pending
     * validation or erroring, so a broken connector stays reachable instead of
     * disappearing into the full catalog.
     */
    ENABLED = 'enabled',
}

/** One connection visible to the caller for a provider/app — the shared connection (if any) plus their own private ones. */
export type ConnectorConnectionSummary = {
    uuid: string;
    visibility: ConnectorVisibility;
    /** Human-readable name — falls back to a generic label when none was set. */
    label: string;
    status: ProjectAppConnectorStatus;
    connectedAt: string | null;
    /** First name or email of the connecting user when known. */
    connectedByName: string | null;
};

/**
 * One external-MCP (Pipedream/Composio) connection visible to the caller —
 * shared public rows plus the caller's own private rows.
 */
export type ExternalMcpConnectionSummary = {
    uuid: string;
    appKey: string;
    visibility: ConnectorVisibility;
    /** Human-readable name — falls back to a generic label when none was set. */
    label: string;
    status: ProjectAppConnectorStatus;
    connectedAt: string | null;
    /** First name or email of the connecting user when known. */
    connectedByName: string | null;
};

export type UpdateConnectorConnectionLabelRequest = {
    label: string | null;
};

/** Connection state of a catalog app for one project. */
export enum ProjectAppConnectorStatus {
    NOT_CONNECTED = 'notConnected',
    PENDING = 'pending',
    ACTIVE = 'active',
    ERROR = 'error',
}

export type ProjectAppConnector = AppConnectorRegistryEntry & {
    status: ProjectAppConnectorStatus;
};

/**
 * Live facts about an app, read from the external provider that connects it
 * when a connector profile is opened.
 *
 * Only ever populated for connectors that genuinely connect through such a
 * provider — an apiRef or genericMcp app is wired up by us against its own API
 * or MCP server, so a provider's tool counts and vendor details would describe
 * a connection that does not exist. Providers with no metadata endpoint report
 * null rather than a placeholder.
 */
export type ExternalAppMetadata = {
    /** The provider's canonical id for the app — resolved, never derived. */
    slug: string;
    name: string;
    description: string | null;
    logoUrl: string | null;
    appUrl: string | null;
    toolsCount: number | null;
};

/** Connection state for one access lane (shared or personal). */
export type ConnectorAccessLane = {
    connectedAt: string | null;
    status: ProjectAppConnectorStatus;
};

export type AppConnectorSharedAccess = ConnectorAccessLane & {
    /** First name of the admin who connected the shared row, when known. */
    connectedByName: string | null;
};

export type AppConnectorPersonalAccess = ConnectorAccessLane & {
    /** Members other than the caller holding their own private connection. */
    otherPrivateUserCount: number;
};

/**
 * How this app is connected in this project, from the caller's perspective.
 * Shared and personal lanes are independent — both cards always render.
 */
export type AppConnectorAccess = {
    shared: AppConnectorSharedAccess;
    personal: AppConnectorPersonalAccess;
};

export type AppConnectorProfile = ProjectAppConnector & {
    externalApp: ExternalAppMetadata | null;
    access: AppConnectorAccess;
};

export type ApiAppConnectorProfile = {
    status: 'ok';
    results: AppConnectorProfile;
};

export type AppConnectorRegistryList = {
    data: ProjectAppConnector[];
    paginate: Pagination;
};

export type ApiAppConnectorRegistryList = {
    status: 'ok';
    results: AppConnectorRegistryList;
};

export enum AppConnectorRegistryQueryParams {
    SEARCH = 'search',
    STATUS = 'status',
}

export enum GenericMcpServerStatus {
    PENDING = 'pending',
    ACTIVE = 'active',
    ERROR = 'error',
}

export enum GenericMcpAuthType {
    BEARER = 'bearer',
    API_KEY_HEADER = 'api_key_header',
}

export type GenericMcpServer = {
    uuid: string;
    projectUuid: string;
    appKey: string | null;
    name: string;
    mcpServerUrl: string;
    authType: GenericMcpAuthType;
    authHeaderName: string | null;
    hasCredentials: boolean;
    status: GenericMcpServerStatus;
    lastValidatedAt: string | null;
    createdAt: string;
    updatedAt: string;
    visibility: ConnectorVisibility;
    /** The user who created the server, or null for public servers. */
    connectedByUserUuid: string | null;
};

export type CreateGenericMcpServerRequest = {
    appKey: string | null;
    name: string;
    mcpServerUrl: string;
    authType: GenericMcpAuthType;
    authHeaderName: string | null;
    credentials: string | null;
    /** Defaults to public. */
    visibility: ConnectorVisibility;
};

export type UpdateGenericMcpServerRequest =
    Partial<CreateGenericMcpServerRequest>;

/** Response from the Pipedream Connect session endpoint. */
export type PipedreamConnectSession = {
    token: string;
    connectLinkUrl: string;
    expiresAt: string;
};

export enum DbtProjectType {
    DBT = 'dbt',
    DBT_CLOUD_IDE = 'dbt_cloud_ide',
    GITHUB = 'github',
    GITLAB = 'gitlab',
    BITBUCKET = 'bitbucket',
    AZURE_DEVOPS = 'azure_devops',
    NONE = 'none',
    MANIFEST = 'manifest',
}

export type ProjectAnalytics = {
    analyticsProjectUuid: string;
    dashboardId: string;
    filters: ProjectAnalyticsFilter[];
};

export type ReachabilityConfig = {
    sms?: ReachabilityConfigMapping;
    email?: ReachabilityConfigMapping;
    whatsapp?: ReachabilityConfigMapping;
    slack?: ReachabilityConfigMapping;
    any?: ReachabilityConfigMapping;
    unknown?: ReachabilityConfigMapping;
    push?: ReachabilityConfigMapping;
};

export type ReachabilityConfigMapping = {
    primary: TableConfig;
    secondary: TableConfig[] | undefined;
};

export type TableConfig = {
    tableName: string;
    dimensionName: string;
};

export type ProjectAnalyticsFilter = {
    tableName: string;
    columnName: string;
    parentCampaignIdColumName: string;
    rawEventsTableName: string;
    rawEventsColumnName: string;
    rawEventsParentCampaignIdColumnName: string;
    subscriptionEventsTableName: string;
    subscriptionEventsColumnName: string;
    subscriptionEventsParentCampaignIdColumnName: string;
};

export type ProjectAttributes = {
    analytics?: ProjectAnalytics;
    syncConfig?:
        | {
              cron: string;
              cronId: string;
          }
        | undefined;
    columnUsageStatsConfig?: {
        cron: string;
        cronId: string;
    };
    materializationCleanupConfig?: {
        cron: string;
        cronId: string;
        olderThanDays: number;
    };
    reachability?: ReachabilityConfig;
    exportAbility?: boolean;
    exportToIterableAbility?: boolean;
    exportToBraze?: {
        enabled: boolean;
        token?: string;
        baseUrl?: string;
    };
    fynoWorkspaceAccessToken?: string;
    unsubscribeIdentifier?: UnsubscribeIdTypes;
    isTemplateLanguage?: TemplateLanguage;
    /**
     * When true, every campaign in the project is routed through the native
     * SendGrid connector, whatever its own `useNativeConnector` flag says.
     * When false or absent, the campaign-level flag decides.
     */
    useNativeConnector?: boolean;

    otherCepsConnectingSources?: OtherCepsConnectingSource[];

    openCodeToken?: {
        uuid: string; // Service account UUID for management
        token: string; // Raw token retained server-side for MCP authentication
        expiresAt?: string | null; // Missing or null on legacy non-expiring project tokens
    };
    /**
     * Stable external ID generated at workspace creation for use in AWS IAM role trust policies
     * (STS AssumeRole confused-deputy protection). Exposed to the user on the blob storage page.
     */
    stsExternalId?: string;
};

export enum OtherCepsConnectingSourceType {
    AIRBYTE = 'airbyte',
}

export type OtherCepsConnectingSource = {
    type: OtherCepsConnectingSourceType;
    sourceId: string;
};

export enum TemplateLanguage {
    LIQUID = 'liquid',
    HANDLEBARS = 'handlebars',
}

export enum UnsubscribeIdTypes {
    channelId = 'channelId',
    userId = 'userId',
    hybrid = 'hybrid',
}

export enum WarehouseTypes {
    BIGQUERY = 'bigquery',
    POSTGRES = 'postgres',
    REDSHIFT = 'redshift',
    SNOWFLAKE = 'snowflake',
    DATABRICKS = 'databricks',
    TRINO = 'trino',
    CLICKHOUSE = 'clickhouse',
    ATHENA = 'athena',
}

export const AthenaInternalColumn = '_srt_rn';

export type SshTunnelConfiguration = {
    useSshTunnel?: boolean;
    sshTunnelHost?: string;
    sshTunnelPort?: number;
    sshTunnelUser?: string;
    sshTunnelPublicKey?: string;
    sshTunnelPrivateKey?: string;
};

export enum BigqueryAuthenticationType {
    SSO = 'sso',
    PRIVATE_KEY = 'private_key',
    ADC = 'adc', // Application Default Credentials
}
export type CreateBigqueryCredentials = {
    type: WarehouseTypes.BIGQUERY;
    project: string;
    dataset: string;
    threads?: number;
    timeoutSeconds: number | undefined;
    priority: 'interactive' | 'batch' | undefined;
    authenticationType?: BigqueryAuthenticationType;
    keyfileContents: Record<string, string>; // used for both sso and private key
    requireUserCredentials?: boolean;
    retries: number | undefined;
    location: string | undefined;
    maximumBytesBilled: number | undefined;
    startOfWeek?: WeekDay | null;
    executionProject?: string;
};
export const sensitiveCredentialsFieldNames = [
    'user',
    'password',
    'keyfileContents',
    'personalAccessToken',
    'privateKey',
    'privateKeyPass',
    'sshTunnelPrivateKey',
    'accessKey',
    'secretKey',
    'sslcert',
    'sslkey',
    'sslrootcert',
    'token',
    'refreshToken',
    'oauthClientId',
    'oauthClientSecret',
    'accessKeyId',
    'secretAccessKey',
] as const;
export type SensitiveCredentialsFieldNames =
    (typeof sensitiveCredentialsFieldNames)[number];
export type BigqueryCredentials = Omit<
    CreateBigqueryCredentials,
    SensitiveCredentialsFieldNames
>;

export enum DatabricksAuthenticationType {
    PERSONAL_ACCESS_TOKEN = 'personal_access_token',
    OAUTH_M2M = 'oauth_m2m',
    OAUTH_U2M = 'oauth_u2m',
}

export type CreateDatabricksCredentials = {
    type: WarehouseTypes.DATABRICKS;
    catalog?: string;
    // this supposed to be a `schema` but changing it will break for existing customers
    database: string;
    serverHostName: string;
    httpPath: string;
    authenticationType?: DatabricksAuthenticationType;
    personalAccessToken?: string; // Optional when using OAuth
    refreshToken?: string; // Refresh token for OAuth, used to generate a new access token
    token?: string; // Access token for OAuth, has a low expiry time (1 hour)
    oauthClientId?: string; // OAuth M2M client ID (Service Principal)
    oauthClientSecret?: string; // OAuth M2M client secret (Service Principal)
    requireUserCredentials?: boolean;
    startOfWeek?: WeekDay | null;
    compute?: Array<{
        name: string;
        httpPath: string;
    }>;
};
export type DatabricksCredentials = Omit<
    CreateDatabricksCredentials,
    SensitiveCredentialsFieldNames
>;

export type SslConfiguration = {
    sslmode?: string;
    sslcertFileName?: string;
    sslcert?: string | null; // file content
    sslkeyFileName?: string;
    sslkey?: string | null; // file content
    sslrootcertFileName?: string;
    sslrootcert?: string | null; // file content
};

export type CreatePostgresCredentials = SshTunnelConfiguration &
    SslConfiguration & {
        type: WarehouseTypes.POSTGRES;
        host: string;
        user: string;
        password: string;
        requireUserCredentials?: boolean;
        port: number;
        dbname: string;
        schema: string;
        threads?: number;
        keepalivesIdle?: number;
        searchPath?: string;
        role?: string;
        startOfWeek?: WeekDay | null;
        timeoutSeconds?: number;
    };
export type PostgresCredentials = Omit<
    CreatePostgresCredentials,
    SensitiveCredentialsFieldNames
>;
export type CreateTrinoCredentials = {
    type: WarehouseTypes.TRINO;
    host: string;
    user: string;
    password: string;
    requireUserCredentials?: boolean;
    port: number;
    dbname: string;
    schema: string;
    http_scheme: string;
    source?: string;
    startOfWeek?: WeekDay | null;
};
export type TrinoCredentials = Omit<
    CreateTrinoCredentials,
    SensitiveCredentialsFieldNames
>;
export type CreateClickhouseCredentials = {
    type: WarehouseTypes.CLICKHOUSE;
    host: string;
    user: string;
    password: string;
    requireUserCredentials?: boolean;
    port: number;
    schema: string;
    secure?: boolean;
    startOfWeek?: WeekDay | null;
    timeoutSeconds?: number;
};
export type ClickhouseCredentials = Omit<
    CreateClickhouseCredentials,
    SensitiveCredentialsFieldNames
>;

export type CreateAthenaCredentials = {
    type: WarehouseTypes.ATHENA;
    region: string;
    database: string;
    schema: string;
    s3StagingDir: string;
    s3DataDir?: string;
    accessKeyId: string;
    secretAccessKey: string;
    workGroup?: string;
    threads?: number;
    numRetries?: number;
    requireUserCredentials?: boolean;
    startOfWeek?: WeekDay | null;
};

export type AthenaCredentials = Omit<
    CreateAthenaCredentials,
    SensitiveCredentialsFieldNames
>;

export type CreateRedshiftCredentials = SshTunnelConfiguration & {
    type: WarehouseTypes.REDSHIFT;
    host: string;
    user: string;
    password: string;
    requireUserCredentials?: boolean;
    port: number;
    dbname: string;
    schema: string;
    threads?: number;
    keepalivesIdle?: number;
    sslmode?: string;
    ra3Node?: boolean;
    startOfWeek?: WeekDay | null;
    timeoutSeconds?: number;
};
export type RedshiftCredentials = Omit<
    CreateRedshiftCredentials,
    SensitiveCredentialsFieldNames
>;

// TODO use enum instead
export enum SnowflakeAuthenticationType {
    PASSWORD = 'password',
    PRIVATE_KEY = 'private_key',
    SSO = 'sso',
    USERNAME_PASSWORD = 'username/password',
    EXTERNAL_BROWSER = 'external_browser',
}

export type CreateSnowflakeCredentials = {
    type: WarehouseTypes.SNOWFLAKE;
    account: string;
    user: string;
    password?: string;
    requireUserCredentials?: boolean;
    privateKey?: string;
    privateKeyPass?: string;
    authenticationType?: SnowflakeAuthenticationType;
    refreshToken?: string; // Refresh token for sso, this is used to generate a new access token
    token?: string; // Access token for sso, this has a low expiry time
    role?: string;
    database: string;
    warehouse: string;
    schema: string;
    threads?: number;
    clientSessionKeepAlive?: boolean;
    queryTag?: string;
    accessUrl?: string;
    startOfWeek?: WeekDay | null;
    quotedIdentifiersIgnoreCase?: boolean;
    disableTimestampConversion?: boolean; // Disable timestamp conversion to UTC - only disable if all timestamp values are already in UTC
    override?: boolean;
    organizationWarehouseCredentialsUuid?: string;
};
export type SnowflakeCredentials = Omit<
    CreateSnowflakeCredentials,
    SensitiveCredentialsFieldNames
>;
export type CreateWarehouseCredentials =
    | CreateRedshiftCredentials
    | CreateBigqueryCredentials
    | CreatePostgresCredentials
    | CreateSnowflakeCredentials
    | CreateDatabricksCredentials
    | CreateTrinoCredentials
    | CreateClickhouseCredentials
    | CreateAthenaCredentials;
export type WarehouseCredentials =
    | SnowflakeCredentials
    | RedshiftCredentials
    | PostgresCredentials
    | BigqueryCredentials
    | DatabricksCredentials
    | TrinoCredentials
    | ClickhouseCredentials
    | AthenaCredentials;

export type NonSensitiveBlobCredentials = Omit<
    BlobConfig,
    SensitiveCredentialsFieldNames
>;

export type CreatePostgresLikeCredentials =
    | CreateRedshiftCredentials
    | CreatePostgresCredentials;

export const maybeOverrideWarehouseConnection = <
    T extends WarehouseCredentials,
>(
    connection: T,
    overrides: { schema?: string },
): T => {
    const isBigquery = connection.type === WarehouseTypes.BIGQUERY;
    const overridesSchema = isBigquery
        ? { dataset: overrides.schema }
        : { schema: overrides.schema };
    return {
        ...connection,
        ...(overrides.schema ? overridesSchema : undefined),
    };
};

/**
 * Merges new warehouse credentials with base credentials, preserving advanced settings
 * like requireUserCredentials from the base credentials.
 *
 * This is useful when creating preview projects where we want to use new connection details
 * (like from dbt profiles) but preserve advanced configuration from the parent project.
 */
export const mergeWarehouseCredentials = <T extends CreateWarehouseCredentials>(
    baseCredentials: T,
    newCredentials: T,
): T => {
    // If types don't match, return newCredentials as-is (can't merge different warehouse types)
    if (baseCredentials.type !== newCredentials.type) {
        // eslint-disable-next-line no-console
        console.info(
            `Skipping merge of warehouse credentials due to differing types: ${baseCredentials.type} and ${newCredentials.type}`,
        );
        return newCredentials;
    }

    // Edge case: if the warehouse is snowflake but with a different warehouse, return newCredentials as-is
    // This is to avoid enforcing requireUserCredentials on a different snowflake warehouse that might not have SSO enabled or different roles
    if (
        baseCredentials.type === WarehouseTypes.SNOWFLAKE &&
        newCredentials.type === WarehouseTypes.SNOWFLAKE &&
        baseCredentials.warehouse.toLowerCase().trim() !==
            newCredentials.warehouse.toLowerCase().trim()
    ) {
        // eslint-disable-next-line no-console
        console.info(
            `Skipping merge of Snowflake credentials due to differing warehouse names: ${baseCredentials.warehouse} and ${newCredentials.warehouse}`,
        );
        return newCredentials;
    }

    // Only add non sensitive fields from base credentials to avoid conflicts with authentication methods
    const keysToExclude = [
        ...sensitiveCredentialsFieldNames,
        'authenticationType',
    ];
    const filteredBaseCredentials = Object.fromEntries(
        Object.entries(baseCredentials).filter(
            ([key]) =>
                !keysToExclude.includes(key as SensitiveCredentialsFieldNames),
        ),
    );
    // We will use new credentials for connection, this might contain new authentication method
    // do not include all baseCredentials here, to avoid conflicts on authentication (that will cause a mix of serviceaccounts/sso/passwords)
    const merged = {
        ...filteredBaseCredentials, // We copy most of the base config from the parent project, including advanced settings
        ...newCredentials,
        // Keep requireUserCredentials from base credentials, since this is a security setting and should not be overridden
        requireUserCredentials:
            baseCredentials.requireUserCredentials ||
            newCredentials.requireUserCredentials,
    };

    return merged as T;
};

export interface DbtProjectConfigBase {
    type: DbtProjectType;
}

export type DbtProjectEnvironmentVariable = {
    key: string;
    value: string;
};

export enum SupportedDbtVersions {
    V1_4 = 'v1.4',
    V1_5 = 'v1.5',
    V1_6 = 'v1.6',
    V1_7 = 'v1.7',
    V1_8 = 'v1.8',
    V1_9 = 'v1.9',
    V1_10 = 'v1.10',
    V1_11 = 'v1.11',
}

// Make it an enum to avoid TSOA errors
export enum DbtVersionOptionLatest {
    LATEST = 'latest',
}

export function isDbtVersion110OrHigher(
    version: SupportedDbtVersions | undefined,
): boolean {
    if (!version) {
        return false;
    }
    // Get all enum values as an array in order
    const versions = Object.values(SupportedDbtVersions);
    const v110Index = versions.indexOf(SupportedDbtVersions.V1_10);
    const currentIndex = versions.indexOf(version);

    // If the current version is at or after v1.10 in the enum order
    return currentIndex >= v110Index;
}

export type DbtVersionOption = SupportedDbtVersions | DbtVersionOptionLatest;

export const getLatestSupportDbtVersion = (): SupportedDbtVersions => {
    const versions = Object.values(SupportedDbtVersions);
    return versions[versions.length - 1];
};

export const DefaultSupportedDbtVersion = DbtVersionOptionLatest.LATEST;

export interface DbtProjectCompilerBase extends DbtProjectConfigBase {
    target?: string;
    environment?: DbtProjectEnvironmentVariable[];
    selector?: string;
}

export interface DbtNoneProjectConfig extends DbtProjectCompilerBase {
    type: DbtProjectType.NONE;

    hideRefreshButton?: boolean;
}

export interface DbtManifestProjectConfig extends DbtProjectConfigBase {
    type: DbtProjectType.MANIFEST;
    manifest: string;
    hideRefreshButton: boolean;
}

export interface DbtLocalProjectConfig extends DbtProjectCompilerBase {
    type: DbtProjectType.DBT;
    profiles_dir?: string;
    project_dir?: string;
}

export interface DbtCloudIDEProjectConfig extends DbtProjectConfigBase {
    type: DbtProjectType.DBT_CLOUD_IDE;
    api_key: string;
    environment_id: string;
    discovery_api_endpoint?: string;
    tags?: string[];
}

export interface DbtGithubProjectConfig extends DbtProjectCompilerBase {
    type: DbtProjectType.GITHUB;
    authorization_method: 'personal_access_token' | 'installation_id';
    personal_access_token?: string;
    installation_id?: string;
    repository: string;
    branch: string;
    project_sub_path: string;
    host_domain?: string;
}

export interface DbtGitlabProjectConfig extends DbtProjectCompilerBase {
    type: DbtProjectType.GITLAB;
    personal_access_token: string;
    repository: string;
    branch: string;
    project_sub_path: string;
    host_domain?: string;
}

export interface DbtBitBucketProjectConfig extends DbtProjectCompilerBase {
    type: DbtProjectType.BITBUCKET;
    username: string;
    personal_access_token: string;
    repository: string;
    branch: string;
    project_sub_path: string;
    host_domain?: string;
}

export interface DbtAzureDevOpsProjectConfig extends DbtProjectCompilerBase {
    type: DbtProjectType.AZURE_DEVOPS;
    personal_access_token: string;
    organization: string;
    project: string;
    repository: string;
    branch: string;
    project_sub_path: string;
}

export type DbtProjectConfig =
    | DbtLocalProjectConfig
    | DbtCloudIDEProjectConfig
    | DbtGithubProjectConfig
    | DbtBitBucketProjectConfig
    | DbtGitlabProjectConfig
    | DbtAzureDevOpsProjectConfig
    | DbtNoneProjectConfig
    | DbtManifestProjectConfig;

export const isGitProjectType = (
    connection: DbtProjectConfig,
): connection is
    | DbtGithubProjectConfig
    | DbtBitBucketProjectConfig
    | DbtGitlabProjectConfig =>
    [
        DbtProjectType.GITHUB,
        DbtProjectType.GITLAB,
        DbtProjectType.BITBUCKET,
    ].includes(connection.type);

const isRemoteType = (
    connection: DbtProjectConfig,
): connection is DbtLocalProjectConfig | DbtCloudIDEProjectConfig =>
    [DbtProjectType.DBT, DbtProjectType.DBT_CLOUD_IDE].includes(
        connection.type,
    );

export const maybeOverrideDbtConnection = <T extends DbtProjectConfig>(
    connection: T,
    overrides: {
        branch?: string;
        environment?: DbtProjectEnvironmentVariable[];
        manifest?: string;
    },
): T => {
    // If manifest is provided, create a MANIFEST connection type
    if (overrides.manifest) {
        return {
            type: DbtProjectType.MANIFEST,
            manifest: overrides.manifest,
            hideRefreshButton: true,
        } as T;
    }

    return {
        ...connection,
        ...(isGitProjectType(connection) && overrides.branch
            ? { branch: overrides.branch }
            : undefined),
        ...(!isRemoteType(connection) && overrides.environment
            ? { environment: overrides.environment }
            : undefined),
    };
};

export type Project = {
    organizationUuid: string;
    projectUuid: string;
    name: string;
    type: ProjectType;
    dbtConnection: DbtProjectConfig;
    warehouseConnection?: WarehouseCredentials | null;
    pinnedListUuid?: string;
    upstreamProjectUuid?: string;
    needsRelation?: boolean;
    isBaseTableConfigured?: boolean;
    fynoWorkspaceId: string | null;
    blobConnection: NonSensitiveBlobCredentials | null;
    timezone: Timezones | null;
    attributes: ProjectAttributes | null;
    dbtVersion: DbtVersionOption;
    schedulerTimezone: string;
    createdByUserUuid: string | null;
    isDemo: boolean;
    organizationWarehouseCredentialsUuid?: string;
};

export type ProjectSummary = Pick<
    Project,
    | 'name'
    | 'projectUuid'
    | 'organizationUuid'
    | 'type'
    | 'fynoWorkspaceId'
    | 'timezone'
    | 'attributes'
    | 'upstreamProjectUuid'
>;

export type ApiProjectResponse = {
    status: 'ok';
    results: Project;
};

export type ApiGetProjectGroupAccesses = {
    status: 'ok';
    results: ProjectGroupAccess[];
};

export type IdContentMapping = {
    id: number | string;
    newId: number | string;
};

export type PreviewContentMapping = {
    charts: IdContentMapping[];
    chartVersions: IdContentMapping[];
    spaces: IdContentMapping[];
    dashboards: IdContentMapping[];
    dashboardVersions: IdContentMapping[];
    savedSql: IdContentMapping[];
    savedSqlVersions: IdContentMapping[];
    aiAgents: IdContentMapping[];
};

export type CreateProjectRequest = Required<
    Pick<Project, 'name' | 'timezone'>
> &
    Partial<Project>;

export type BaseProject = Pick<
    Project,
    'projectUuid' | 'name' | 'timezone' | 'organizationUuid' | 'fynoWorkspaceId'
>;

export enum ProjectSettings {
    overview = 'ProjectSettingsOverview',
    dataConnection = 'ProjectSettingsDataConnection',
    blobStorage = 'ProjectSettingsBlobStorage',
    syncSchedules = 'ProjectSettingsSyncSchedules',
    channels = 'ProjectSettingsChannels',
    subscriptionGroups = 'ProjectSettingsSubscriptionGroups',
    deliveryControls = 'ProjectSettingsDeliveryControls',
    eventSources = 'ProjectSettingsEventSources',
    members = 'ProjectSettingsMembers',
    reachabilityVariables = 'ProjectSettingsReachabilityVariables',
}

export type UpdateSchedulerSettings = {
    schedulerTimezone: string;
};
